import { config } from '../../config';

/**
 * Identifikátory v adresách a v tabuľkách.
 *
 * Ten istý záznam má v tejto aplikácii tri podoby a všetky tri sú legitímne:
 *
 * | podoba | kde |
 * | --- | --- |
 * | `urn:ngsi-ld:presov.sk:digital-obcan:pdnt:MQRJ73F` | na drôte (rieši [[urnApiWrapper]]) |
 * | `pdnt:MQRJ73F` | v appke - to, čo vracia API klient |
 * | `MQRJ73F` | v adrese a v stĺpci ID |
 *
 * Tretia podoba je tu preto, že prvé dve v adrese nič nepridávajú: cesta
 * `/administracia/podnety/MQRJ73F/nalezy/MPJW8JG` už povie, že prvé je
 * podnet a druhé nález, takže `pdnt:` a `nlz:` v nej boli len šum. Adresa
 * ale musí zniesť **všetky tri** - odkazy s plným URN chodia z notifikácií
 * a z e-mailov a nesmú skončiť na 404.
 *
 * Preto sa v celej appke drží pravidlo: **do adresy a do bunky tabuľky ide
 * `shortId`, z adresy sa číta cez `entityId`.** Kdekoľvek inde (telo dotazu,
 * `rowKey`, výber riadkov) sa pracuje s appkovou podobou - tam sa nekráti nič.
 */

/**
 * Predpony entít DigiObčana. Zdroj pravdy je `platform.entities_prefix`
 * v konfigurácii `digiobcan-api` (a v `news-creator` pre novinky) - tam sa
 * identifikátory generujú. Kategórie podnetov predponu zdieľajú s kategóriami
 * noviniek (`cat`), ale žijú v iných cestách, takže sa nemíňajú.
 */
export const ID_PREFIX = {
  report: 'pdnt',
  issue: 'nlz',
  project: 'prj',
  news: 'spr',
  category: 'cat',
  voting: 'vot',
} as const;

export type IdKind = keyof typeof ID_PREFIX;

const URN_PREFIX = `${config.API_URN_PREFIXES.DigiobcanApi}:`;

/**
 * Posledný segment identifikátora - to jediné, čím sa záznam odlišuje.
 * Patrí do adresy a do stĺpca ID; do dotazu nikdy.
 */
export const shortId = (id?: string): string =>
  id ? id.slice(id.lastIndexOf(':') + 1) : '';

/**
 * Identifikátor z adresy do podoby, s akou pracuje appka (`pdnt:MQRJ73F`).
 *
 * Prijíma všetky tri tvary z tabuľky vyššie. Neznámy tvar sa nezahadzuje -
 * doplní sa mu predpona a nech o ňom rozhodne API; 404 z neho je poctivejšia
 * odpoveď než biela stránka z `undefined`.
 *
 * Práve tým prežili odkazy spred premenovania ohlásení na podnety: e-maily
 * a notifikácie rozposlané s `…:ohls:MQRJ73F` sem prídu ako neznámy tvar,
 * sedemznakový sufix je pritom rovnaký a v databáze ho prepísala migrácia
 * `000015`, takže z nich vyjde platné `pdnt:MQRJ73F`. Nepridávaj sem preto
 * zoznam povolených predpôn - fungovalo by to, kým by niekto taký zoznam
 * neupratal.
 *
 * **Pozor na sentinely v adresách.** Niektoré obrazovky „nový záznam" nie sú
 * vlastnou routou, ale tým istým detailom s vyhradenou hodnotou namiesto
 * identifikátora (`/administracia/hlasovania/nove`,
 * `/administracia/projekty/novy`, `/administracia/vyhody/nova`). Taká hodnota
 * je pre `entityId` neznámy tvar ako každý iný, takže z `nove` urobí
 * `vot:nove` - a test `id === 'nove'` po nej **nikdy nevyjde**. Sentinel sa
 * preto porovnáva na surovom parametri z `useParams` a `entityId` sa volá až
 * na skutočné ID. Keď sa to pomiešalo, vytváranie hlasovaní aj projektov
 * skončilo na 404 z detailu, ktorý sa nemal vôbec pýtať.
 */
export function entityId(raw: string, kind: IdKind): string;
export function entityId(
  raw: string | undefined,
  kind: IdKind,
): string | undefined;
export function entityId(
  raw: string | undefined,
  kind: IdKind,
): string | undefined {
  if (!raw) return undefined;

  const naked = raw.startsWith(URN_PREFIX) ? raw.slice(URN_PREFIX.length) : raw;
  const prefix = ID_PREFIX[kind];

  return naked.startsWith(`${prefix}:`) ? naked : `${prefix}:${shortId(naked)}`;
}
